import React, { useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';

import { ModalAddVisualizationProps } from 'apps/marsinet/src/app/models/visualizationModels';
import { useAddVisualizationValidationSchema } from 'apps/marsinet/src/app/validations/addVisualizationValidationSchema';
import { DataSourceDto, MetricDto, VisualizationDto, VisualizationDtoTypeEnum } from 'apps/marsinet/src/app/typescript-axios';
import { VISUALIZATION_CONSTRAINTS } from '../visualizations/config';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { ToggleButton } from 'primereact/togglebutton';
import { Chips } from 'primereact/chips';
import { MultiSelect } from 'primereact/multiselect';

import { canSubmit, getFormErrorMessage, handleNextStep, handlePreviousStep, isFormFieldInvalid, onPageHide, Step, stepHasChanges, stepIsValid } from '../../utils/form_utils';
import { FormStepFooter } from '../../utils/forms-step-footer';
import { ColumnAliasMapping, mapColumnAlias } from '../../utils/datasource_utils';


const INITIAL_VALUES = {
    visualization: {
        type: undefined,
        data_source: undefined,
        position: { x: 0, y: 0 },
        size: { w: 1, h: 1 },
        parameters: {},
    } as VisualizationDto,
};


const STEPS: Step[] = [
    {
        label: 'pages.dashboards.visualization.type',
        fields: ['visualization.type'],
        required: true,
    },
    {
        label: 'pages.dashboards.visualization.selectDataSource',
        fields: ['visualization.data_source'],
        required: true,
    },
    {
        label: 'pages.dashboards.visualization.parameters',
        fields: ['visualization.parameters'],
        required: true,
    },
];

const ModalAddVisualization: React.FC<ModalAddVisualizationProps> = ({
    visible,
    setVisible,
    availableDataSources,
    availableMetrics,
    createVisualizationFunction,
}) => {
    const { t } = useTranslation();
    const [activeStep, setActiveStep] = useState(0);

    const formik = useFormik({
        initialValues: INITIAL_VALUES,
        validationSchema: useAddVisualizationValidationSchema([
            ...availableDataSources.map((ds: DataSourceDto) => ds.name),
            ...availableMetrics.map((m: MetricDto) => m.name),
        ]), validateOnMount: false,
        onSubmit: async (values, { resetForm }) => {
            try {
                await createVisualizationFunction(values);
                resetForm();
                setActiveStep(0);
                setVisible(false);
            } catch (error) {
                console.error('Something is wrong with the Visualization:', error);
            }
        },
    });

    const isCurrentStepRequired = STEPS[activeStep].required;

    const isNextDisabled = isCurrentStepRequired
        ? !stepHasChanges(STEPS[activeStep].fields, formik.values, formik.initialValues) ||
        !stepIsValid(STEPS[activeStep].fields, formik.errors)
        : false;

    const isSkipping = !isCurrentStepRequired && !stepHasChanges(STEPS[activeStep].fields, formik.values, formik.initialValues);

    const isLastStep = activeStep === STEPS.length - 1;


    const visualizationTypeOptions = Object.values(VisualizationDtoTypeEnum).map((type) => ({
        label: t(getVisualizationLabel(type)),
        value: type,
        icon: getVisualizationIcon(type),
    }));

    const dataSourceOptions = availableDataSources.map((ds: DataSourceDto) => ({
        label: ds.name,
        value: ds.name,
    }));

    const metricOptions = availableMetrics.map((metric: MetricDto) => ({
        label: metric.name,
        value: metric.name,
    }));

    const selectedType = formik.values.visualization.type;
    const parameterSchema = selectedType
        ? VISUALIZATION_CONSTRAINTS[selectedType]?.parameterSchema
        : null;

    // Get columns from selected data source for string/string[] pickers
    const selectedDataSource = availableDataSources.find(item => item.name === formik.values.visualization.data_source);
    let columnOptions;
    if (selectedDataSource) {
        const availableColumnsAliasMapping: ColumnAliasMapping = mapColumnAlias(selectedDataSource?.query_definition?.columns, selectedDataSource?.query_definition?.group_by);
        columnOptions = Object.values(availableColumnsAliasMapping).map(alias => ({
            label: alias,
            value: alias
        }));
    }
    else {
        columnOptions = [];
    }

    const visualization_form_name: string = "visualization-form";

    const footer = <FormStepFooter
        activeStep={activeStep}
        isLastStep={isLastStep}
        isSkipping={isSkipping}
        isNextDisabled={isNextDisabled}
        canSubmit={canSubmit(formik.initialValues, formik.values, formik.isValid)}
        onPrevious={() => handlePreviousStep(activeStep, STEPS, setActiveStep, formik)}
        onNext={() => handleNextStep(activeStep, STEPS, isSkipping, setActiveStep, formik)}
        labels={{
            back: t('pages.dashboards.back'),
            next: t('pages.dashboards.next'),
            skip: t('pages.dashboards.skip'),
            create: t('pages.dashboards.visualization.create'),
        }}
        form_name={visualization_form_name}
    />;

    return (
        <Dialog
            header={t('pages.dashboards.visualization.create')}
            visible={visible}
            className="mx-2 md:w-6 lg:w-4"
            onHide={() => onPageHide(formik, setActiveStep, setVisible)}
            breakpoints={{ '960px': '75vw', '641px': '100vw' }}
            blockScroll={true}
            footer={footer}
        >
            <div className="mb-3 text-lg font-semibold text-900">
                {t(STEPS[activeStep].label)}
            </div>

            <form
                id={visualization_form_name}
                autoComplete="off"
                onSubmit={formik.handleSubmit}
            >
                <div className="grid formgrid p-fluid">

                    {/* ── Step 1: Select Visualization Type ── */}
                    {activeStep === 0 && (() => {
                        const unidimensionalOptions = visualizationTypeOptions.filter(
                            (option) => VISUALIZATION_CONSTRAINTS[option.value]?.data_type === 'metric'
                        );
                        const multidimensionalOptions = visualizationTypeOptions.filter(
                            (option) => VISUALIZATION_CONSTRAINTS[option.value]?.data_type !== 'metric'
                        );

                        const renderOption = (option: typeof visualizationTypeOptions[0]) => (
                            <div
                                key={option.value}
                                className={`
                flex align-items-center gap-3 p-3 border-round-lg cursor-pointer border-2 transition-all transition-duration-150
                ${formik.values.visualization.type === option.value
                                        ? 'border-primary bg-primary-50'
                                        : 'border-200 surface-card hover:border-primary-300'
                                    }
            `}
                                onClick={async () => {
                                    await formik.setFieldValue('visualization.data_source', undefined);
                                    await formik.setFieldValue('visualization.type', option.value);
                                    formik.setFieldTouched('visualization.type', true, true);
                                    const type = option.value;
                                    if (type) {
                                        const constraints = VISUALIZATION_CONSTRAINTS[type];
                                        await formik.setFieldValue('visualization.size', { w: constraints.defaultW, h: constraints.defaultH });
                                        if (!constraints?.parameterSchema) return;
                                        const defaultParams: Record<string, any> = {};
                                        for (const [key, def] of Object.entries(constraints.parameterSchema)) {
                                            if ('default' in def) defaultParams[key] = (def as any).default;
                                        }
                                        await formik.setFieldValue('visualization.parameters', defaultParams);
                                    }
                                }}
                            >
                                <i className={`${option.icon} text-2xl ${formik.values.visualization.type === option.value ? 'text-primary' : 'text-500'}`} />
                                <span className={`font-medium ${formik.values.visualization.type === option.value ? 'text-primary' : 'text-700'}`}>
                                    {option.label}
                                </span>
                                {formik.values.visualization.type === option.value && (
                                    <i className="pi pi-check ml-auto text-primary" />
                                )}
                            </div>
                        );

                        return (
                            <div className="field col-12">

                                {unidimensionalOptions.length > 0 && (
                                    <div className="mb-3">
                                        <div className="text-sm font-semibold text-500 uppercase mb-2">
                                            {t('pages.dashboards.visualization.unidimensional')}
                                        </div>
                                        <div className="flex flex-column gap-2">
                                            {unidimensionalOptions.map(renderOption)}
                                        </div>
                                    </div>
                                )}

                                {multidimensionalOptions.length > 0 && (
                                    <div>
                                        <div className="text-sm font-semibold text-500 uppercase mb-2">
                                            {t('pages.dashboards.visualization.multidimensional')}
                                        </div>
                                        <div className="flex flex-column gap-2">
                                            {multidimensionalOptions.map(renderOption)}
                                        </div>
                                    </div>
                                )}

                                {isFormFieldInvalid('visualization.type', formik.touched, formik.errors) && (
                                    <small className="p-error block mt-1">
                                        {getFormErrorMessage('visualization.type', formik.touched, formik.errors)}
                                    </small>
                                )}
                            </div>
                        );
                    })()}

                    {/* ── Step 2: Select Data Source or Metric ── */}
                    {activeStep === 1 && (() => {
                        const selectedType = formik.values.visualization.type;
                        const isMetricType = selectedType
                            ? VISUALIZATION_CONSTRAINTS[selectedType].data_type == 'metric'
                            : false;

                        return (
                            <div className="field col-12">
                                {isMetricType ? (
                                    <>
                                        <Dropdown
                                            id="input-visualization-metric"
                                            name="visualization.data_source"
                                            value={formik.values.visualization.data_source}
                                            options={metricOptions}
                                            onChange={async (e) => {
                                                await formik.setFieldValue('visualization.data_source', e.value);
                                                formik.setFieldTouched('visualization.data_source', true, true);
                                            }}
                                            className={
                                                isFormFieldInvalid('visualization.data_source', formik.touched, formik.errors) ? 'p-invalid' : ''
                                            }
                                            style={{ width: '100%' }}
                                            optionLabel="label"
                                            filter
                                            emptyMessage={t('pages.dashboards.visualization.noMetrics')}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <Dropdown
                                            id="input-visualization-datasource"
                                            name="visualization.data_source"
                                            value={formik.values.visualization.data_source}
                                            options={dataSourceOptions}
                                            onChange={async (e) => {
                                                await formik.setFieldValue('visualization.data_source', e.value);
                                                formik.setFieldTouched('visualization.data_source', true, true);
                                            }}
                                            className={
                                                isFormFieldInvalid('visualization.data_source', formik.touched, formik.errors) ? 'p-invalid' : ''
                                            }
                                            style={{ width: '100%' }}
                                            optionLabel="label"
                                            filter
                                            emptyMessage={t('pages.dashboards.visualization.noDataSources')}
                                        />
                                    </>
                                )}
                                {isFormFieldInvalid('visualization.data_source', formik.touched, formik.errors) && (
                                    <small className="p-error block mt-1">
                                        {getFormErrorMessage('visualization.data_source', formik.touched, formik.errors)}
                                    </small>
                                )}
                            </div>
                        );
                    })()}

                    {/* ── Step 3: Dynamic Parameters ── */}
                    {activeStep === 2 && parameterSchema && (
                        <>
                            {Object.entries(parameterSchema).map(([paramKey, paramDef]) => {
                                const fieldPath = `visualization.parameters.${paramKey}`;
                                const fieldValue = formik.values.visualization.parameters?.[paramKey];

                                return (
                                    <div key={paramKey} className="field col-12">
                                        <label htmlFor={`param-${paramKey}`} className="font-medium mb-1 block capitalize">
                                            {paramKey.replace(/_/g, ' ')}
                                            {paramDef.required && <span className="text-red-500 ml-1">*</span>}
                                        </label>

                                        {paramDef.type === 'boolean' && (
                                            <ToggleButton
                                                id={`param-${paramKey}`}
                                                checked={fieldValue ?? paramDef.default ?? false}
                                                onChange={(e) =>
                                                    formik.setFieldValue(fieldPath, e.value)
                                                }
                                                onLabel={t('yes')}
                                                offLabel={t('no')}
                                                style={{ width: '100%' }}
                                            />
                                        )}

                                        {/* number → InputNumber */}
                                        {paramDef.type === 'number' && (
                                            <InputNumber
                                                id={`param-${paramKey}`}
                                                value={fieldValue ?? null}
                                                onValueChange={(e) =>
                                                    formik.setFieldValue(fieldPath, e.value)
                                                }
                                                onBlur={() => formik.setFieldTouched(fieldPath, true)}
                                                className={isFormFieldInvalid(fieldPath, formik.touched, formik.errors) ? 'p-invalid' : ''}
                                                style={{ width: '100%' }}
                                                min={0}
                                            />
                                        )}

                                        {/* column → Dropdown with columns */}
                                        {paramDef.type === 'column' && (
                                            <Dropdown
                                                id={`param-${paramKey}`}
                                                value={fieldValue ?? null}
                                                options={columnOptions}
                                                onChange={(e) =>
                                                    formik.setFieldValue(fieldPath, e.value)
                                                }
                                                onBlur={() => formik.setFieldTouched(fieldPath, true)}
                                                className={isFormFieldInvalid(fieldPath, formik.touched, formik.errors) ? 'p-invalid' : ''}
                                                style={{ width: '100%' }}
                                                filter
                                                showClear={!paramDef.required}
                                            />
                                        )}

                                        {/* string → Dropdown if allowedValues, else InputText */}
                                        {paramDef.type === 'string' && (
                                            paramDef.allowedValues ? (
                                                <Dropdown
                                                    id={`param-${paramKey}`}
                                                    value={fieldValue ?? null}
                                                    options={paramDef.allowedValues.map((v: string) => ({ label: v, value: v }))}
                                                    onChange={(e) =>
                                                        formik.setFieldValue(fieldPath, e.value)
                                                    }
                                                    onBlur={() => formik.setFieldTouched(fieldPath, true)}
                                                    className={isFormFieldInvalid(fieldPath, formik.touched, formik.errors) ? 'p-invalid' : ''}
                                                    style={{ width: '100%' }}
                                                    showClear={!paramDef.required}
                                                />
                                            ) : (
                                                <InputText
                                                    id={`param-${paramKey}`}
                                                    value={fieldValue ?? ''}
                                                    onChange={(e) =>
                                                        formik.setFieldValue(fieldPath, e.target.value)
                                                    }
                                                    onBlur={() => formik.setFieldTouched(fieldPath, true)}
                                                    className={isFormFieldInvalid(fieldPath, formik.touched, formik.errors) ? 'p-invalid' : ''}
                                                    style={{ width: '100%' }}
                                                />
                                            )
                                        )}

                                        {/* column[] → MultiSelect with columns*/}
                                        {paramDef.type === 'column[]' && (
                                            <MultiSelect
                                                id={`param-${paramKey}`}
                                                value={fieldValue ?? []}
                                                options={columnOptions}
                                                onChange={(e) =>
                                                    formik.setFieldValue(fieldPath, e.value)
                                                }
                                                onBlur={() => formik.setFieldTouched(fieldPath, true)}
                                                className={isFormFieldInvalid(fieldPath, formik.touched, formik.errors) ? 'p-invalid' : ''}
                                                display="chip"
                                                style={{ width: '100%' }}
                                                filter
                                            />
                                        )}

                                        {paramDef.type === 'string[]' && (
                                            paramDef.allowedValues ? (
                                                <MultiSelect
                                                    id={`param-${paramKey}`}
                                                    value={fieldValue ?? []}
                                                    options={paramDef.allowedValues.map((v: string) => ({ label: v, value: v }))}
                                                    onChange={(e) =>
                                                        formik.setFieldValue(fieldPath, e.value)
                                                    }
                                                    onBlur={() => formik.setFieldTouched(fieldPath, true)}
                                                    className={isFormFieldInvalid(fieldPath, formik.touched, formik.errors) ? 'p-invalid' : ''}
                                                    display="chip"
                                                    style={{ width: '100%' }}
                                                    filter
                                                />
                                            ) : (
                                                <Chips
                                                    id={`param-${paramKey}`}
                                                    value={fieldValue ?? []}
                                                    onChange={(e) =>
                                                        formik.setFieldValue(fieldPath, e.value)
                                                    }
                                                    onBlur={() => formik.setFieldTouched(fieldPath, true)}
                                                    className={isFormFieldInvalid(fieldPath, formik.touched, formik.errors) ? 'p-invalid' : ''}
                                                    style={{ width: '100%' }}
                                                />
                                            )
                                        )}

                                        {isFormFieldInvalid(fieldPath, formik.touched, formik.errors) && (
                                            <small className="p-error block mt-1">
                                                {getFormErrorMessage(fieldPath, formik.touched, formik.errors)}
                                            </small>
                                        )}
                                    </div>
                                );
                            })}
                        </>
                    )}

                    {formik.status?.submitError && (
                        <div className="col-12 flex justify-content-center align-items-center text-red-500">
                            {formik.status.submitError}
                        </div>
                    )}
                </div>
            </form>
        </Dialog>
    );
};

function getVisualizationLabel(type: VisualizationDtoTypeEnum): string {
    switch (type) {
        case VisualizationDtoTypeEnum.Table:
            return 'pages.dashboards.visualization.tableType';
        case VisualizationDtoTypeEnum.LineChart:
            return 'pages.dashboards.visualization.lineChartType';
        case VisualizationDtoTypeEnum.AreaChart:
            return 'pages.dashboards.visualization.areaChartType';
        case VisualizationDtoTypeEnum.BarChart:
            return 'pages.dashboards.visualization.barChartType';
        case VisualizationDtoTypeEnum.Card:
            return 'pages.dashboards.visualization.cardType';
        default:
            return 'N/A';
    }
}

// Maps VisualizationDtoTypeEnum values to PrimeIcons
function getVisualizationIcon(type: VisualizationDtoTypeEnum): string {
    switch (type) {
        case VisualizationDtoTypeEnum.Table:
            return 'pi pi-table';
        case VisualizationDtoTypeEnum.LineChart:
            return 'pi pi-chart-line';
        case VisualizationDtoTypeEnum.AreaChart:
            return 'pi pi-chart-line';
        case VisualizationDtoTypeEnum.BarChart:
            return 'pi pi-chart-bar';
        case VisualizationDtoTypeEnum.Card:
            return 'pi pi-id-card';
        default:
            return 'pi pi-question-circle';
    }
}

export default ModalAddVisualization;